import React, { useState } from 'react';
import api, { authService } from '../../services/api';
import { STORAGE_KEYS } from '../../constants/storage';
import { TenantHeaderTest } from './TenantHeaderTest';

const RefreshTokenTest: React.FC = () => {
  const [testResult, setTestResult] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [testSteps, setTestSteps] = useState<string[]>([]);

  const addTestStep = (step: string) => {
    setTestSteps(prev => [...prev, `${new Date().toLocaleTimeString()}: ${step}`]);
  };

  const handleTestRefreshToken = async () => {
    setLoading(true);
    setTestSteps([]);
    addTestStep('Starting refresh token test...');

    try {
      addTestStep('Making request with invalid token...');
      const result = await authService.testRefreshToken();
      addTestStep(`Request completed with status: ${result.success ? 'Success' : 'Failed'}`);
      setTestResult(JSON.stringify(result, null, 2));
    } catch (error: any) {
      addTestStep('Error occurred during test');
      setTestResult(JSON.stringify(error, null, 2));
    } finally {
      setLoading(false);
      addTestStep('Test completed');
    }
  };

  /** Test the real app flow: 401 → interceptor → refresh-token API → retry or redirect */
  const handleTestInterceptorFlow = async () => {
    setLoading(true);
    setTestSteps([]);
    const savedToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!savedToken) {
      addTestStep('You must be logged in. Log in first, then run this test.');
      setTestResult(JSON.stringify({ error: 'Not logged in' }, null, 2));
      setLoading(false);
      return;
    }
    addTestStep('Saved current token. Setting invalid token to force 401...');
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, 'invalid_token_force_401');
    addTestStep('Calling API through app (interceptor will run on 401)...');
    addTestStep('Open Network tab: look for 401 → refresh-token → then retry or redirect.');

    try {
      const response = await api.get('/api/Student/Student-Enrolled-Courses', {
        params: { studentId: '61205712-b563-44ae-b208-be53b776848d', pagenumber: 1, pagesize: 10 },
      });
      addTestStep(`Retry succeeded: ${response.status}`);
      setTestResult(JSON.stringify({ success: true, status: response.status, data: response.data }, null, 2));
    } catch (error: any) {
      addTestStep(`Request failed: ${error.message}`);
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        addTestStep('Refresh likely failed (invalid token). You may be redirected to login.');
      }
      setTestResult(
        JSON.stringify(
          {
            success: false,
            error: error.message,
            status: error.response?.status,
            details: error.response?.data,
          },
          null,
          2
        )
      );
      // Restore token so user can stay on test page if not redirected
      if (savedToken) {
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, savedToken);
        addTestStep('Restored your token (if you were not redirected).');
      }
    } finally {
      setLoading(false);
      addTestStep('Test completed.');
    }
  };

  const clearResults = () => {
    setTestResult('');
    setTestSteps([]);
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Refresh Token Test</h2>
      
      <div className="space-y-4">
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handleTestRefreshToken}
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? 'Testing...' : 'Test refresh API (manual)'}
          </button>
          <button
            onClick={handleTestInterceptorFlow}
            disabled={loading}
            className="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"
          >
            {loading ? 'Testing...' : 'Test 401 → auto refresh (interceptor)'}
          </button>
          <button
            onClick={clearResults}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Clear Results
          </button>
        </div>

        {testSteps.length > 0 && (
          <div className="mt-4">
            <h3 className="text-lg font-semibold mb-2">Test Steps:</h3>
            <div className="bg-gray-100 p-4 rounded">
              {testSteps.map((step, index) => (
                <div key={index} className="mb-1">
                  {step}
                </div>
              ))}
            </div>
          </div>
        )}

        {testResult && (
          <div className="mt-4">
            <h3 className="text-lg font-semibold mb-2">Test Result:</h3>
            <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
              {testResult}
            </pre>
          </div>
        )}

        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <h3 className="text-lg font-semibold mb-2">How to Test:</h3>
          <ol className="list-decimal list-inside space-y-2">
            <li>Make sure you're <strong>logged in</strong></li>
            <li>Open DevTools (F12) → <strong>Network</strong> tab</li>
            <li>
              <strong>Test refresh API (manual):</strong> Calls refresh-token directly, then retries. Good to verify the refresh endpoint works.
            </li>
            <li>
              <strong>Test 401 → auto refresh (interceptor):</strong> Uses the app API with an invalid token so the first request returns 401. The interceptor should call refresh-token and then retry or redirect. If refresh fails (e.g. backend rejects invalid token), you may be redirected to login — log in again to continue testing.
            </li>
            <li>In Network you should see: first request (401) → <code>refresh-token</code> request → then either the original request retried with new token, or redirect to login.</li>
          </ol>
        </div>
      </div>
      
      {/* Add Tenant Header Test Component */}
      <div className="mt-8">
        <TenantHeaderTest />
      </div>
    </div>
  );
};

export default RefreshTokenTest; 